import { DeliveryChannel } from '../giftCoupon/giftCoupon.interface';

/**
 * 발송매체(도매 사이트) 충전금 미러 원장 — 스펙: docs/gift-channel-ledger-spec.md
 *
 * 잔액은 별도 캐시 문서 없이 채널별 amount 합산(append-only 원장 단일 소스).
 * "우리 잔액 == 실제 사이트 잔액" 대조가 어긋나는 순간이 곧 실수
 * (도매가 오입력·입금 미기록·무관사용 미기록)의 신호다.
 */

// amount 부호 규약: Deposit/Restore = +, Deduct/ExternalUse = −, Adjust = ± 자유
export enum GiftChannelLedgerType {
  Deposit = 'Deposit', // 충전 입금 (수동)
  Deduct = 'Deduct', // 발송 차감 (자동 기장 — 삭제 불가)
  ExternalUse = 'ExternalUse', // 우리 사이트와 무관한 사용 (수동 출금)
  Restore = 'Restore', // 채널 취소·환급 (수동 입금)
  Adjust = 'Adjust', // 보정 (기초잔액 세팅 포함, memo 필수)
}

export const LEDGER_TYPE_CONFIG: Record<
  GiftChannelLedgerType,
  { label: string; badgeClass: string }
> = {
  [GiftChannelLedgerType.Deposit]: {
    label: '충전 입금',
    badgeClass: 'border-blue-300 bg-blue-50 text-blue-700',
  },
  [GiftChannelLedgerType.Deduct]: {
    label: '발송 차감',
    badgeClass: 'border-gray-300 bg-gray-50 text-gray-600',
  },
  [GiftChannelLedgerType.ExternalUse]: {
    label: '무관 사용',
    badgeClass: 'border-orange-300 bg-orange-50 text-orange-700',
  },
  [GiftChannelLedgerType.Restore]: {
    label: '취소·환급',
    badgeClass: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  },
  [GiftChannelLedgerType.Adjust]: {
    label: '보정',
    badgeClass: 'border-purple-300 bg-purple-50 text-purple-700',
  },
};

/**
 * 수동 입력 타입 — 삭제 허용 대상.
 * 단 Deduct와 자동 기장분(source 존재 — 발송취소 환급 Restore, 교정 Adjust 등)은
 * 서버가 400 거부하므로 삭제 UI는 `!entry.source`까지 함께 확인할 것.
 */
export const MANUAL_LEDGER_TYPES: readonly GiftChannelLedgerType[] = [
  GiftChannelLedgerType.Deposit,
  GiftChannelLedgerType.ExternalUse,
  GiftChannelLedgerType.Restore,
  GiftChannelLedgerType.Adjust,
];

// undeliver는 Deduct가 아니라 발송취소 자동 환급(Restore)에 붙는다
// — 스펙: docs/gift-coupon-undeliver-spec.md
export type GiftChannelDeductSource =
  | 'deliver'
  | 'proxyDeliver'
  | 'renewal'
  | 'repair'
  | 'correction'
  | 'undeliver';

export const DEDUCT_SOURCE_LABEL: Record<GiftChannelDeductSource, string> = {
  deliver: '발송',
  proxyDeliver: '대리발송',
  renewal: '연장 발급',
  repair: '소급 기장',
  correction: '사후 교정',
  undeliver: '발송취소 환급',
};

export interface LedgerCreatedBy {
  _id: string;
  username?: string;
  info?: { name?: string };
}

// occurredAt 등 날짜는 인터셉터(handleAxiosDates)가 런타임에 Date로 변환한다
export interface GiftChannelLedgerEntry {
  _id: string;
  channel: DeliveryChannel;
  type: GiftChannelLedgerType;
  /** 부호 있는 금액(원) */
  amount: number;
  /** 실제 발생 시각(입금일/발송시각) — 정렬·기간 기준 */
  occurredAt: Date;
  memo?: string;
  createdBy?: LedgerCreatedBy;

  // Deduct 전용 (amount = −unitPriceWhole × quantity)
  unitPriceWhole?: number;
  quantity?: number;
  couponIds?: string[];
  /** 목록(listLedger)은 name populate — 구서버 응답은 id 문자열일 수 있음 */
  giftItems?: Array<{ _id: string; name?: string } | string>;
  source?: GiftChannelDeductSource;

  createdAt?: Date;
  /** 목록 응답 한정 — 이 항목 반영 후 누적 잔액 */
  balanceAfter?: number;
}

/** 목록 행 표기용 상품명 — populate 안 된 구서버 응답(id 문자열)은 건너뜀 */
export const ledgerGiftItemNames = (entry: GiftChannelLedgerEntry): string[] =>
  (entry.giftItems ?? []).flatMap((item) =>
    typeof item === 'object' && item.name ? [item.name] : [],
  );

export interface ChannelBalanceSummary {
  channel: DeliveryChannel;
  balance: number;
  entryCount: number;
  lastOccurredAt: Date | null;
  /** 강한 경고 신호(입금 미기록/도매가 오입력)지만 발송은 차단되지 않음 */
  isNegative: boolean;
}

export interface ChannelLedgerListResponse {
  channel: DeliveryChannel;
  /** from 이전까지의 기초잔액 (from 없으면 0) */
  openingBalance: number;
  closingBalance: number;
  entries: GiftChannelLedgerEntry[];
}

export interface ChannelReconcileResponse {
  channel: DeliveryChannel;
  ourBalance: number;
  actualBalance: number;
  /** actualBalance − ourBalance */
  diff: number;
  matched: boolean;
  recentEntries: GiftChannelLedgerEntry[];
}

export interface UnrecordedDeductGroup {
  /** null이면 채널 미상 — recordable: false */
  deliveryChannel: DeliveryChannel | null;
  priceWhole: number | null;
  deliveredAt: Date;
  couponIds: string[];
  giftItemIds: string[];
  quantity: number;
  amount: number;
  recordable: boolean;
}

export interface UnrecordedResponse {
  totalCoupons: number;
  groups: UnrecordedDeductGroup[];
}

export interface RepairUnrecordedResponse {
  repairedGroups: number;
  repairedCoupons: number;
  skippedGroups: number;
  skippedCoupons: number;
}

/** 수동 기록 공통 DTO — amount는 양수(원), 부호는 type이 결정 (Adjust만 ± 자유) */
export interface ChannelLedgerManualDto {
  channel: DeliveryChannel;
  amount: number;
  /** ISO 문자열 — 생략 시 서버 현재시각 */
  occurredAt?: string;
  memo?: string;
}
